import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';

import { Platform } from '@angular/cdk/platform';
import {
  Input,
  OnInit,
  Output,
  NgZone,
  SkipSelf,
  Optional,
  Component,
  ViewChild,
  OnDestroy,
  ElementRef,
  forwardRef,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostListener,
  ViewContainerRef,
  AfterViewInit,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';

import { SnAbilityService } from '@lib/ability';
import { SnStreamSources, SnLiveVideoUrls, SnStreamSourceList, SnCamera } from '@lib/api/entities';
import { SnCamerasAPI, SnIntegrationsAPI } from '@lib/api/services';
import { SnCurrentOrganization, SnCurrentUser, SnSessionService } from '@lib/auth';
import { SnAspectRatio } from '@lib/common/enums';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { ISnPlayer, SnPlayerGroupDirective, SnPlayerState } from '@lib/player/common';

import { PtzControlsService } from './ptz-controls.service';
import {
  SnHlsPlayerReference,
  SnSldpPlayerReference,
  SnHtml5PlayerReference,
  FsFlvPlayerReference,
} from './references';
import { WebSocketPlayerReference } from './references/websocket-player.reference';
import { StreamAnalysisService } from './services/stream-analysis-service';
import { VideoPlaybackService } from './video-playback.service';
import { SnVideoPlayerReference } from './video-player.reference';

export enum KEY_CODE {
  FF_MINUS = 173,
  EQUALS = 187,
}

enum PlayerProtocol {
  HLS = 'hls',
  FLV = 'flv',
  SLDP = 'sldp',
  HTML5 = 'html5',
  WEBSOCKET = 'websocket',
  UNKNOWN = 'unknown',
}

@Component({
  selector: 'sn-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: 'SN_PLAYER',
      useExisting: forwardRef(() => SnVideoPlayerComponent),
    },
    PtzControlsService,
  ],
  host: {
    class: 'sn-video-player',
  },
})
export class SnVideoPlayerComponent implements OnInit, OnDestroy, ISnPlayer, AfterViewInit {
  @Input()
  get src(): any | SnLiveVideoUrls | SnStreamSources {
    return this._src;
  }
  set src(value: any | SnLiveVideoUrls | SnStreamSources) {
    this._src = value;
    setTimeout(() => {
      this._ngZone.runOutsideAngular(() => {
        this._destroy();
        this.initialization();
      });
    }, 0);
  }
  @Input()
  get muted(): boolean {
    return this._muted;
  }
  set muted(value: boolean) {
    this._muted = value;
    if (this._reference) {
      this._reference.muted = value;
    }
  }
  @Input()
  get live(): boolean {
    return this._live$.value;
  }
  set live(value: boolean) {
    this._live$.next(value);
    this._changeDetectorRef.detectChanges();
    if (this._reference) {
      this._reference.live = value;
    }
  }

  @Output() public readonly stateChange = new EventEmitter<SnPlayerState>();

  get streamResolution(): any {
    return this._reference.videoResolution;
  }

  get video(): any {
    const videoElement = this.elementRef.nativeElement;
    return videoElement.querySelector('video');
  }

  get parent(): any {
    return this.video;
  }

  get live$(): Observable<boolean> {
    return this._live$.asObservable();
  }

  get state$(): Observable<SnPlayerState> {
    return this._state$.asObservable();
  }

  get play$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Playing));
  }

  get pause$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Pause));
  }

  get end$(): Observable<SnPlayerState> {
    return this._state$.pipe(filter((state) => state === SnPlayerState.Ended));
  }

  get ended$(): Observable<void> {
    return this._ended$.asObservable();
  }

  get currentTime$(): Observable<number> {
    return this._currentTime$.asObservable();
  }

  get loaded$(): Observable<boolean> {
    return this._loaded$.asObservable();
  }

  get bufferTime$(): Observable<number> {
    return this._bufferedTime$.asObservable();
  }

  get totalTime$(): Observable<number> {
    return this._totalTime$.asObservable();
  }

  get volume$(): Observable<number> {
    return this._volume$.asObservable();
  }

  get connected$(): Observable<boolean> {
    return this._connected$.asObservable();
  }

  get played$(): Observable<null> {
    return this._played$.asObservable();
  }

  get fullscreen$(): Observable<boolean> {
    return this._fullscreen$.asObservable();
  }

  get ptzOn$(): Observable<boolean> {
    return this._ptzOn$.asObservable();
  }

  get fragLoading$(): Observable<void> {
    return this._fragLoading$.asObservable();
  }

  get fragLoaded$(): Observable<void> {
    return this._fragLoaded$.asObservable();
  }

  get currentTime(): number {
    return this._currentTime$.value;
  }

  get actualTime(): number | undefined {
    return this._reference?.actualTime;
  }

  get reference(): SnVideoPlayerReference {
    return this._reference;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get isPlaying(): boolean {
    return this._reference ? this._reference.isPlaying : false;
  }

  get isHls(): boolean {
    return this._reference instanceof SnHlsPlayerReference;
  }

  @ViewChild('myDiv') public myDiv: ElementRef<HTMLElement>;
  @ViewChild(ToastContainerDirective) public toastContainer: ToastContainerDirective;
  @ViewChild('timeStampDisplay')
  public timeStampDisplayElementRef: ElementRef<any>;

  @Input()
  public caption: string;
  @Input()
  public subEnabled: boolean;
  @Input()
  public splashScreen: string;
  @Input()
  public autoplay: boolean;
  @Input()
  public loopDuration: number;
  @Input()
  public controls = true;
  @Input()
  public cloudTimeout: boolean = true;
  @Input()
  public width: string | number;
  @Input()
  public height: string | number;
  @Input()
  public aspectRatio: number | SnAspectRatio;
  @Input()
  public main: boolean;
  @Input()
  public local: boolean;
  @Input()
  public ptzConfig: any;
  @Input()
  public timestamp: any = true;
  @Input()
  public isFlv: boolean = false;
  @Input()
  public isFususCamera: boolean = false;
  @Input()
  public alwaysOn = false;
  @Input()
  public isFmp4Src: boolean;
  @Input()
  public camId;
  @Input()
  public camera?: SnCamera;
  @Input()
  public elevation: number;
  @Input()
  public time: Date;
  @Output()
  public readonly init = new EventEmitter<ISnPlayer>();
  @Output()
  public readonly fullscreenChange = new EventEmitter<boolean>();
  @Output()
  public readonly playerError = new EventEmitter<ISnPlayer>();
  public fullscreen = false;
  public ptzOn = true;
  public startExpire = false;
  public expire = false;
  public displayedColumns: string[] = ['presetNumber', 'presetName', 'actions'];
  public currentEmailUser = null;
  public deleting: any;
  public playerState: SnPlayerState;
  public crosshair: boolean;
  public isClipping: boolean = false;
  public isFmp4: boolean = false;
  public selectDate: boolean = false;
  public videoCurrentTime: number;
  public showEvents: boolean;
  public isAreaZoomDrawing: boolean = false;
  public protocol: PlayerProtocol = PlayerProtocol.UNKNOWN;

  public isHidden: boolean = true;
  public tabHidden: boolean = false;
  public page = 0;
  public readonly canView: boolean;
  public timeStamp;
  public errorCount: number = 0;
  public retryCount: number = 0;
  public _wsPlayerElement: HTMLDivElement;
  public fragLoaded: boolean = false;
  public levelLoaded: boolean = false;
  @ViewChild(MatPaginator) public paginator: MatPaginator;

  private _muted = false;
  private _src: string | SnLiveVideoUrls | SnStreamSources;
  private _reference: SnVideoPlayerReference;
  private _videoElement: HTMLVideoElement;
  private readonly _live$ = new BehaviorSubject<boolean>(false);
  private readonly _ended$ = new Subject<void>();
  private readonly _currentTime$ = new BehaviorSubject<number>(0);
  private readonly _loaded$ = new Subject<boolean>();
  private readonly _bufferedTime$ = new BehaviorSubject<number>(0);
  private readonly _totalTime$ = new BehaviorSubject<number>(0);
  private readonly _volume$ = new BehaviorSubject<number>(0);
  private readonly _state$ = new BehaviorSubject<SnPlayerState>(SnPlayerState.Pending);
  private readonly _destroy$ = new Subject<void>();
  private readonly _fragLoading$ = new Subject<void>();
  private readonly _fragLoaded$ = new Subject<void>();
  private readonly _connected$ = new BehaviorSubject<boolean>(false);
  private readonly _played$ = new BehaviorSubject<null>(null);
  private readonly _fullscreen$ = new BehaviorSubject<boolean>(false);
  private readonly _ptzOn$ = new BehaviorSubject<boolean>(false);
  private _playbackStartTime: number | null = null;

  private _isLoading: boolean = false;
  @ViewChild('player', { read: ElementRef, static: true })
  private readonly _playerElementRef: ElementRef;

  @ViewChild('playerContent', { read: ElementRef, static: true })
  private readonly _playerContentElementRef: ElementRef<HTMLElement>;

  constructor(
    public elementRef: ElementRef,
    public viewContainerRef: ViewContainerRef,
    private readonly _ngZone: NgZone,
    private readonly _platform: Platform,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    public currentOrganization: SnCurrentOrganization,
    private readonly _currentUser: SnCurrentUser,
    private readonly _ability: SnAbilityService,
    private readonly _toastrService: ToastrService,
    private readonly _datadogService: FsDataDogRumService,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _playbackService: VideoPlaybackService,
    private readonly _sessionService: SnSessionService,
    private readonly _integrationsAPI: SnIntegrationsAPI,
    private readonly _streamAnalysisService: StreamAnalysisService,
    @Optional() @SkipSelf() private readonly _group: SnPlayerGroupDirective,
  ) {
    this.canView = this._ability.can({ subject: 'cameras/camera', action: 'view' });
  }

  private get _isEenV3ApiEnabled(): boolean {
    const eagleeyenetworksIntegration =
      this._integrationsAPI.getIntegrationByCode('eagleeyenetworks');
    return (
      eagleeyenetworksIntegration?.status === 'active' &&
      eagleeyenetworksIntegration?.meta?.['v3ApiEnabled'] === true
    );
  }

  @HostListener('document:keydown.space')
  public togglePause(): void {
    if ((!this.fullscreen && !this.alwaysOn) || this.isAreaZoomDrawing) {
      return;
    }
    if (this.playerState === SnPlayerState.Playing) {
      this.pause();
    } else if (this.playerState === SnPlayerState.Pause) {
      if (this._reference.isRewinding) {
        this._playbackService.rewindPause();
      } else {
        this.play();
      }
    }
  }

  @HostListener('document:fullscreenchange', ['$event'])
  public onFullscreenChange(): void {
    if (!document.fullscreenElement) {
      this.exitedFullscreen();
    } else {
      this._toastrService.overlayContainer = this.toastContainer;
    }
  }

  public ngOnInit(): void {
    this.currentOrganization.settings$.pipe(takeUntil(this._destroy$)).subscribe();
    this.currentEmailUser = this._currentUser.email;
    this._state$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      this.playerState = state;
      this.stateChange.next(state);
      this._changeDetectorRef.detectChanges();
    });

    this.currentTime$.pipe(takeUntil(this._destroy$)).subscribe((time) => {
      this._updateTimestamp();
    });

    this._integrationsAPI.checkIntegrations().pipe(takeUntil(this._destroy$)).subscribe();
  }

  public ngOnDestroy(): void {
    if (!this.live && this._reference && !this._reference.loaded) {
      this._emitPlaybackAttemptEnd('canceled');
    }

    this._state$.complete();
    this._ended$.complete();
    this._currentTime$.complete();
    this._loaded$.complete();
    this._bufferedTime$.complete();
    this._totalTime$.complete();
    this._volume$.complete();
    this._destroy$.next();
    this._destroy$.complete();
    this._destroy();
  }

  private _emitPlaybackAttemptEnd(
    status: 'success' | 'fail' | 'canceled',
    error?: {
      raw_error?: string;
      error_code?: string;
      error_source?: string;
      is_canceled?: boolean;
    },
  ): void {
    if (!this.live && this.camId && this._playbackStartTime) {
      const streamContext = {
        ...(this.local
          ? this._streamAnalysisService.getStreamContextEnd(this.camera, false)
          : this._streamAnalysisService.getStreamContextEnd(this.camera, true)),
        live: this.live,
      };
      this._datadogService.addAction('playback_attempt_end', {
        id: this.camId,
        camera_id: this.camId,
        duration_ms: Date.now() - this._playbackStartTime,
        status,
        ...(status === 'canceled' && { is_canceled: true }),
        ...error,
        ...streamContext,
      });
      this._playbackStartTime = null;
    }
  }

  public ngAfterViewInit(): void {
    this._updateTimestamp();
  }

  public toggleControls(): void {
    this.isHidden = !this.isHidden;
    this.tabHidden = !this.tabHidden;
  }

  public play(): void {
    this._reference?.resetPlayback();
    this._reference?.play();
  }

  public pause(): void {
    this._reference?.resetPlayback();
    this._reference?.pause();
  }

  public forward(amount: number): void {
    this.videoCurrentTime = this._reference.currentTime;
    this._reference.currentTime = this.videoCurrentTime + amount;
  }

  public fastForward(): void {
    this._reference.fastForward();
  }

  public rewind(): void {
    this._reference.rewind();
  }

  public back(amount: number): void {
    this.videoCurrentTime = this._reference.currentTime;
    this._reference.currentTime = this.videoCurrentTime - amount;
  }

  public changeVolume(value: number): void {
    this._reference.volume = value;
  }

  public changeCurrentTime(time: number): void {
    this._reference.currentTime = time;
  }

  public setPTZ(on: boolean): void {
    this.ptzOn = on;
    this._ptzOn$.next(on);
  }

  public destroyStream(bypassGroup?: boolean): void {
    this._destroy(bypassGroup);
  }

  public changeFullscreen(ptzOn?: boolean): void {
    if (ptzOn && this.ptzConfig) {
      this._ptzOn$.next(ptzOn);
    }
    this.fullscreen = !this.fullscreen;
    this._reference.toggleFullscreen(this.fullscreen);
    this._fullscreen$.next(this.fullscreen);
    this.fullscreenChange.next(this.fullscreen);
    if (typeof this.src !== 'string' && this.subEnabled && this.live && !this.isFlv) {
      this._destroy();
      this.initialization();
    }
  }

  public exitedFullscreen(): void {
    if (!this.live) {
      this._emitPlaybackAttemptEnd('canceled');
    }

    this._toastrService.clear();
    this._toastrService.overlayContainer = null;
    this._ptzOn$.next(false);
    this.fullscreen = false;
    this._fullscreen$.next(this.fullscreen);
    this.fullscreenChange.next(this.fullscreen);
    if (typeof this.src !== 'string' && this.subEnabled && this.live && !this.isFlv) {
      this._destroy();
      this.initialization();
    }
  }

  public initialization(): void {
    this.expire = false;
    this.startExpire = false;
    this._changeDetectorRef.detectChanges();
    if (this.src === null) {
      return;
    }

    this._isLoading = true;
    if (this.src && typeof this.src === 'string') {
      this._reference = this._sessionService.isFederal
        ? this._initHlsPlayerRef(this.src, () => this._loaded())
        : this.isFlv
        ? this._handleFlvSrc(this.src)
        : this._initHtml5PlayerRef(this.src, () => this._loaded());
      this._afterSrcInit();
    } else if (this.src?.mp4) {
      this._reference = this._initHtml5PlayerRef(this.src?.mp4, () => this._loaded());
      this._afterSrcInit();
    } else if (this.src?.sldp && !this._platform.IOS && !this._platform.ANDROID) {
      this._reference = this._initSldpPlayerRef(
        this.src?.sldp,
        () => this._loaded(),
        () => this._error(),
      );
      this._afterSrcInit();
    } else if (this.src?.hls) {
      this._reference = this._platform.SAFARI
        ? this._initHtml5PlayerRef(this.src?.hls, () => this._loaded())
        : this._initHlsPlayerRef(this.src?.hls, () => this._loaded());
      this._afterSrcInit();
    } else if (this.src?.fmp4) {
      this._reference = this._platform.SAFARI
        ? this._initHtml5PlayerRef(this.src?.fmp4, () => this._loaded())
        : this._initHlsPlayerRef(this.src?.fmp4, () => this._loaded());
      this._afterSrcInit();
    } else if (this.fullscreen || this.main || this.alwaysOn) {
      this._handleStreamsAssign(this.local, this.src?.main);
      this._afterSrcInit();
    } else if (!this.fullscreen) {
      if (this.src?.main?.cloud?.flv || this.src?.main?.cloud?.hls) {
        this._handleStreamsAssign(this.local, this.src?.main);
      } else {
        this._handleStreamsAssign(this.local, this.src?.sub);
      }
      this._afterSrcInit();
    } else {
      console.error(`
        Unsupported VideoPlayerComponent source options.
        Supported sources: mp4, sldp, hls.
      `);
      return;
    }
  }

  public timeIsEnd(event: any): void {
    if (event.action === 'done') {
      this.expire = true;
      this._destroy();
      this.startExpire = false;
      this._changeDetectorRef.detectChanges();
    }
  }

  public getNewUrl(): void {
    this._getNewUrl();
  }

  public getBoundingVideoRect(): any {
    if (!this._videoElement) {
      return {};
    }
    const width = this._playerElementRef.nativeElement.offsetWidth;
    const height = this._playerElementRef.nativeElement.offsetHeight;
    const videoWidth = this._videoElement.videoWidth;
    const videoHeight = this._videoElement.videoHeight;
    const rect: any = {
      top: 0,
      left: 0,
      width,
      height,
    };

    if (height < videoHeight) {
      const scale = height / videoHeight;
      const scaledWidth = videoWidth * scale;
      rect.left = (width - scaledWidth) / 2;
      rect.width = scaledWidth;
    } else if (width < videoWidth) {
      const scale = width / videoWidth;
      const scaledHeight = videoHeight * scale;
      rect.top = (height - scaledHeight) / 2;
      rect.width = scaledHeight;
    }
    return rect;
  }

  private _handleFlvSrc(src: string): FsFlvPlayerReference {
    return this._initFlvPlayerRef(src, () => this._loaded());
  }

  private _updateTimestamp(): void {
    const timeStamp = this.time;
    if (this.timeStampDisplayElementRef) {
      this.timeStampDisplayElementRef.nativeElement.innerHTML = timeStamp
        ? `${timeStamp.toLocaleTimeString()}`
        : null;
    }
  }

  private _afterSrcInit(): void {
    if (!this.local && this.cloudTimeout) {
      setTimeout(() => {
        this.startExpire = true;
        this._changeDetectorRef.detectChanges();
      }, 60 * 60 * 1000 - 30 * 1000);
    }

    if (this._group) {
      this._group.items.add(this);
    }
    if (this.splashScreen) {
      this._reference.splashString = this.splashScreen;
    }
    if (this._reference) {
      this._reference.muted = this._muted;
    }
    if (!this.live && this.camId) {
      this._playbackStartTime = Date.now();
      const streamContext = {
        ...this._streamAnalysisService.getStreamContextStart(this.camera),
        live: this.live,
      };
      const metricData = {
        id: this.camId,
        camera_id: this.camId,
        timestamp: this._playbackStartTime,
        source_ui: 'video_player_mount',
        ...streamContext,
      };
      this._datadogService.addAction('playback_attempt_start', {
        ...metricData,
      });
    }

    this.init.next(this);
    this._initHlsListeners();
  }

  private _initWebsocketPlayerRef(src: string, callback: Function): WebSocketPlayerReference {
    if (!this._wsPlayerElement) {
      this._createWsPlayer();
    }
    this.protocol = PlayerProtocol.WEBSOCKET;

    return new WebSocketPlayerReference(
      this._playerElementRef.nativeElement,
      this._wsPlayerElement,
      src,
      this._platform,
      this._state$,
      this._currentTime$,
      this._bufferedTime$,
      this._totalTime$,
      this._volume$,
      this._played$,
      this._loaded$,
      this._ended$,
      this.autoplay,
      callback,
      this._datadogService,
      this.camId,
    );
  }

  private _initHtml5PlayerRef(src: string, callback: Function): SnHtml5PlayerReference {
    if (!this._videoElement) {
      this._createVideoElement();
    }
    this.protocol = PlayerProtocol.HTML5;

    return new SnHtml5PlayerReference(
      this._playerElementRef.nativeElement,
      this._videoElement,
      src,
      this._platform,
      this._state$,
      this._currentTime$,
      this._bufferedTime$,
      this._totalTime$,
      this._volume$,
      this._played$,
      this._loaded$,
      this._ended$,
      this.autoplay,
      callback,
      this._datadogService,
      this.camId,
    );
  }

  private _initFlvPlayerRef(
    url: string,
    callback: Function,
    bearer?: string,
  ): FsFlvPlayerReference {
    if (!this._videoElement) {
      this._createVideoElement();
    }
    this.protocol = PlayerProtocol.FLV;

    return new FsFlvPlayerReference(
      this._playerElementRef.nativeElement,
      this._videoElement,
      url,
      this._platform,
      this._state$,
      this._currentTime$,
      this._bufferedTime$,
      this._totalTime$,
      this._volume$,
      this._played$,
      this._loaded$,
      this._ended$,
      this.autoplay,
      callback,
      bearer,
      this._datadogService,
      this.camId,
    );
  }

  private _initSldpPlayerRef(
    src: string,
    callback: Function,
    error: Function,
  ): SnSldpPlayerReference {
    this.protocol = PlayerProtocol.SLDP;

    return new SnSldpPlayerReference(
      this._playerElementRef.nativeElement,
      this._playerContentElementRef.nativeElement,
      src,
      this.autoplay,
      this._platform,
      this._state$,
      this._currentTime$,
      this._bufferedTime$,
      this._totalTime$,
      this._volume$,
      this._played$,
      this._loaded$,
      this._ended$,
      callback,
      error,
      this._datadogService,
      this.camId,
      this._currentUser.settings?.userGlobalInterfaceConfig?.sldpSettings,
      this._streamAnalysisService,
      this.camera,
    );
  }

  private _initHlsPlayerRef(src: string, callback: any): SnHlsPlayerReference {
    if (!this._videoElement) {
      this._createVideoElement();
    }
    this.protocol = PlayerProtocol.HLS;
    const source: 'cloud' | 'local' = this.local ? 'local' : 'cloud';
    const streamContext = {
      ...this._streamAnalysisService.getStreamContextEnd(this.camera, !this.local),
      protocol: this.protocol,
      source,
      camera_id: this.camId,
      source_ui: 'video_player_mount',
      live: this.live,
    };
    return new SnHlsPlayerReference(
      this._playerElementRef.nativeElement,
      this._videoElement,
      src,
      this.autoplay,
      this._platform,
      this._state$,
      this._currentTime$,
      this._bufferedTime$,
      this._totalTime$,
      this._volume$,
      this._played$,
      this._loaded$,
      this._ended$,
      this._datadogService,
      callback,
      streamContext,
      () => this._error(),
    );
  }

  private _destroy(bypassGroup?: boolean): void {
    if (this._reference) {
      this._reference.destroy();
    }
    if (this._videoElement) {
      if (this._wsPlayerElement) {
        this._playerContentElementRef.nativeElement.removeChild(this._wsPlayerElement);
        this._wsPlayerElement = null;
      } else {
        this._playerContentElementRef.nativeElement.removeChild(this._videoElement);
      }
      this._videoElement = undefined;
    }
    if (this._group && !bypassGroup) {
      this._group.items.delete(this);
    }
  }

  private _createWsPlayer(): void {
    this._wsPlayerElement = document.createElement('div');
    const id = `fs__websocket-player-${uuid.v4()}`;
    this._wsPlayerElement.id = id;
    this._playerContentElementRef.nativeElement.appendChild(this._wsPlayerElement);
  }

  private _createVideoElement(): void {
    this._videoElement = document.createElement('video');
    this._videoElement.id = uuid.v4();
    this._videoElement.muted = this._muted;
    this._videoElement.autoplay = this.autoplay;
    this._videoElement.tabIndex = -1;
    this._videoElement.setAttribute('playsinline', 'true');
    this._videoElement.setAttribute('crossorigin', 'anonymous');
    this._playerContentElementRef.nativeElement.appendChild(this._videoElement);
  }

  private _loaded(): void {
    if (this._wsPlayerElement) {
      const video = this._wsPlayerElement.querySelector('video');
      if (video) {
        this._videoElement = video;
      }
    }
    this._isLoading = false;
    this._connected$.next(true);
    this._changeDetectorRef.detectChanges();
  }

  private _error(): void {
    this._isLoading = false;
    this._connected$.next(false);
    this.errorCount += 1;
    this.playerError.next(this);
    this._changeDetectorRef.detectChanges();

    if (!this.live && this._playbackStartTime && this.errorCount === 1) {
      this._emitPlaybackAttemptEnd('fail', {
        raw_error: 'HLS playback error',
        error_code: 'PLAYBACK_ERROR',
        error_source: 'video_player',
      });
    }

    if (this.errorCount === 5) {
      this.retryCount += 1;
      if (this.retryCount < 3) {
        this.errorCount = 0;
      }
      if (this.retryCount === 3) {
        this._emitPlaybackAttemptEnd('fail', {
          raw_error: 'Player error occurred',
          error_code: `MAX_RETRIES_EXCEEDED_${this.retryCount}`,
          error_source: 'video_player',
        });
        this._destroy();
      }
    }
    if (this.errorCount < 5) {
      setTimeout(() => {
        this._destroy();
        this.initialization();
      }, 15000);
    }
    if (this.live) {
      this._failedToliveStream();
    }
  }

  private _handleStreamsAssign(local: boolean, source: SnStreamSourceList): void {
    if (!source) {
      return;
    }

    if (local) {
      this._handleLocalStreamsAssign(source);
    } else {
      this._handleCloudStreamsAssign(source);
    }
  }

  private _handleLocalStreamsAssign(source: SnStreamSourceList): void {
    if (source.local?.sldp && !this._platform.IOS && !this._platform.ANDROID) {
      this._reference = this.isFususCamera
        ? this._initHlsPlayerRef(source.local?.hls, () => this._loaded())
        : this._initSldpPlayerRef(
            source.local?.sldp,
            () => this._loaded(),
            () => this._error(),
          );
      return;
    }

    if (source.local?.ws) {
      this._reference = this._initSldpPlayerRef(
        source.cloud?.sldp,
        () => this._loaded(),
        () => this._error(),
      );
      return;
    }

    if (source.local?.hls) {
      this._reference = this._platform.SAFARI
        ? this._initHtml5PlayerRef(source.local.hls, () => this._loaded())
        : this._initHlsPlayerRef(source.local.hls, () => this._loaded());
      return;
    }

    if (source.local?.fmp4) {
      this.isFmp4 = true;
      this._reference = this._initHtml5PlayerRef(source.local.fmp4, () => this._loaded());
      this._changeDetectorRef.detectChanges();
      return;
    }
  }

  private _handleCloudStreamsAssign(source: SnStreamSourceList): void {
    if (source.cloud?.sldp && !this._platform.IOS && !this._platform.ANDROID) {
      this._reference = this._initSldpPlayerRef(
        source.cloud.sldp,
        () => this._loaded(),
        () => this._error(),
      );
      return;
    }

    if (source.cloud?.ws) {
      this._reference = this._initWebsocketPlayerRef(source.cloud.ws, () => this._loaded());
      return;
    }

    if (source.cloud?.hls) {
      this._reference = this._platform.SAFARI
        ? this._initHtml5PlayerRef(source.cloud.hls, () => this._loaded())
        : this._initHlsPlayerRef(source.cloud.hls, () => this._loaded());
      return;
    }

    if (source.cloud?.fmp4) {
      this.isFmp4 = true;
      this._reference = this._initHtml5PlayerRef(source.cloud.fmp4, () => this._loaded());
      this._changeDetectorRef.detectChanges();
      return;
    }

    if (source.cloud?.flv) {
      if (this._isEenV3ApiEnabled) {
        // New v3 API: Use header-based authentication with bearer token
        const srcUrl = source.cloud.flv;
        const bearer = source.cloud.headers.Authorization;
        this._reference = this._initFlvPlayerRef(srcUrl, () => this._loaded(), bearer);
      } else {
        // Original v2 API: Use URL-based authentication (legacy behavior)
        const baseUrl = source.cloud.flv.split('&')[0];
        const headers = source.cloud.headers;
        const startTimestamp = 'stream_1713371108100091c3';
        const endTimestamp = '+300000';
        const authKey = headers['Cookie']
          ?.split('; ')
          ?.find((value) => value?.includes('auth_key'))
          .split('=')[1];
        const srcUrl = `${baseUrl}&start_timestamp=${startTimestamp}&end_timestamp=${endTimestamp}&A=${authKey}`;
        this._reference = this._initFlvPlayerRef(srcUrl, () => this._loaded());
      }
      this._changeDetectorRef.detectChanges();
      return;
    }
  }

  private _failedToliveStream(): void {
    const cameraId = this.camId;
    const cameraName = this.caption;
    this._datadogService.addAction('live_stream_error', {
      id: cameraId,
      name: cameraName,
    });
  }

  private _getNewUrl(): void {
    this._camerasAPI
      .getCamera(this.camId)
      .pipe(takeUntil(this._destroy$))
      .subscribe((cam) => {
        this.src = cam.liveVideoUrls;
        this._changeDetectorRef.detectChanges();
      });
  }

  private _initHlsListeners(): void {
    if (this._reference instanceof SnHlsPlayerReference) {
      this._reference.fragLoading$
        .pipe(takeUntil(this._reference.destroyed$), takeUntil(this._destroy$))
        .subscribe(() => {
          this._fragLoading$.next();
        });

      this._reference.levelLoaded$
        .pipe(takeUntil(this._reference.destroyed$), takeUntil(this._destroy$))
        .subscribe(() => {
          this.levelLoaded = true;
        });

      this._reference.fragLoaded$
        .pipe(takeUntil(this._reference.destroyed$), takeUntil(this._destroy$))
        .subscribe(() => {
          this._fragLoaded$.next();
          this.fragLoaded = true;
        });
    }
  }
}
